import EmbeddedFBPost from 'components/EmbeddedFBPost/EmbeddedFBPost'
import EmbeddedLIPost from 'components/EmbeddedLIPost'
import EmbeddedRedditPost from 'components/EmbeddedRedditPost'
import EmbeddedTweet from 'components/EmbeddedTweet'
import EmbeddedYTPost from 'components/EmbeddedYTPost/EmbeddedYTPost'
import { PPDialog } from 'components/PPDialog'
import {
  Article,
  FEED_TYPE_FACEBOOK,
  FEED_TYPE_KEYWORD,
  FEED_TYPE_LINKEDIN,
  FEED_TYPE_REDDIT,
  FEED_TYPE_TWITTER,
  Gif,
  Photo,
  Video
} from 'interfaces'
import { useState } from 'react'
import { connect } from 'react-redux'
import { closeContentPreviewWindow } from 'services/contentPreview'
import { contentPreviewActiveSelector, contentPreviewContentSelector } from 'services/contentPreview/selectors'
import { parseUrlDomain } from 'shared/utils'
import StoreState, { StoreDispatch } from 'store/state'

import SimpleLoader from 'components/SimpleLoader'
import styles from './ContentPreviewWindow.pcss'

interface ConnectedContentPreviewWindowProps {
  active: boolean
  content?: Photo | Video | Article | Gif
  close: () => void
}

export interface ContentPreviewWindowProps extends ConnectedContentPreviewWindowProps { }

export function ContentPreviewWindow(props: ContentPreviewWindowProps) {
  const [loading, setLoading] = useState(true)
  const contentItem = props.content
  const hasContent = Boolean(contentItem)
  const socialLink = hasContent ? contentItem!.socialLink : ''
  const isTwitter = contentItem && contentItem.feed.type === FEED_TYPE_TWITTER
  const isFacebook = contentItem && contentItem.feed.type === FEED_TYPE_FACEBOOK
  const isReddit = contentItem && contentItem.feed.type === FEED_TYPE_REDDIT
  const isLinkedIn = contentItem && contentItem.feed.type === FEED_TYPE_LINKEDIN
  const isRedditGalleryImage = isReddit && (contentItem as Photo).isRedditGallery
  const isYouTube = contentItem && contentItem.feed.type === FEED_TYPE_KEYWORD && ((/youtube/i)).test(parseUrlDomain(socialLink) || '')
  const isSupported = isTwitter || isFacebook || isReddit || isLinkedIn || isYouTube
  // EXPL: only Twitter, YouTube and LinkedIn embeds report back when they finish. Showing the
  // spinner for the others leaves it stuck on top of the embed forever, so gate it on the
  // branches that can actually clear it.
  const showLoader = loading && Boolean(isTwitter || isYouTube || isLinkedIn)

  const close = () => {
    props.close()
    setLoading(true)
  }

  const onContentLoaded = () => {
    setLoading(false)
  }

  const error = !contentItem
    ? <h2>Something went wrong and we couldn't load your content.</h2>
    : null

  return (
    <PPDialog
      open={props.active}
      onClose={close}
      title="Content preview"
      className={styles.dialog}
      data-testid="content-preview-window"
    >
      <div className={styles.body}>
        <div className={styles['player-box']}>
          {
            showLoader && (
              <div className={styles.loader}>
                <SimpleLoader small />
                <div className={styles['loading-message']}>
                  <p>Loading content...</p>
                </div>
              </div>
            )
          }
          {
            hasContent && isFacebook && (
              <EmbeddedFBPost url={socialLink} />
            )
          }
          {
            hasContent && isTwitter && (
              <EmbeddedTweet url={socialLink} onLoaded={onContentLoaded} />
            )
          }
          {
            hasContent && isReddit && (
              <div>
                <EmbeddedRedditPost post={contentItem as any} />
                {isRedditGalleryImage && (
                  <div className={styles['error-box']}>
                    Preview not available.
                  </div>
                )}
              </div>
            )
          }
          {
            hasContent && isYouTube && (
              <EmbeddedYTPost url={socialLink} onReady={onContentLoaded} />
            )
          }
          {
            hasContent && isLinkedIn && (
              <EmbeddedLIPost url={socialLink} onLoaded={onContentLoaded} />
            )
          }
          {
            hasContent && !isSupported && (
              <div className={styles['error-box']}>
                Preview not available.
              </div>
            )
          }
        </div>
        {error}
      </div>
      {Boolean(socialLink) && (
        <div className={styles['source-link']}>
          <a href={socialLink} target="_blank" rel="noopener">See original post.</a>
        </div>
      )}
    </PPDialog>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    active: contentPreviewActiveSelector(state),
    content: contentPreviewContentSelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreDispatch) {
  return {
    close: () => dispatch(closeContentPreviewWindow())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentPreviewWindow)
